import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { MapPin, Plus, Trash2 } from 'lucide-react';

export const ONSITE_KEYS = [
  'booth_number', 'reporting_location', 'entrance_instructions',
  'onsite_directions', 'parking_instructions', 'info_desk_location',
  'arrival_instructions', 'floor_plan_urls',
];

export const emptyOnsite = () => ({
  booth_number: '', reporting_location: '', entrance_instructions: '',
  onsite_directions: '', parking_instructions: '', info_desk_location: '',
  arrival_instructions: '', floor_plan_urls: [],
});

// Build a shift's stored onsite values into the editable shape.
export const onsiteFromShift = (shift = {}) => ({
  booth_number: shift.booth_number || '',
  reporting_location: shift.reporting_location || '',
  entrance_instructions: shift.entrance_instructions || '',
  onsite_directions: shift.onsite_directions || '',
  parking_instructions: shift.parking_instructions || '',
  info_desk_location: shift.info_desk_location || '',
  arrival_instructions: shift.arrival_instructions || '',
  floor_plan_urls: Array.isArray(shift.floor_plan_urls) ? shift.floor_plan_urls : [],
});

// Only-send-what's-set payload (empty strings become null; empty array stays []).
export const onsitePayload = (v = {}) => ({
  booth_number: (v.booth_number || '').trim() || null,
  reporting_location: (v.reporting_location || '').trim() || null,
  entrance_instructions: (v.entrance_instructions || '').trim() || null,
  onsite_directions: (v.onsite_directions || '').trim() || null,
  parking_instructions: (v.parking_instructions || '').trim() || null,
  info_desk_location: (v.info_desk_location || '').trim() || null,
  arrival_instructions: (v.arrival_instructions || '').trim() || null,
  floor_plan_urls: (v.floor_plan_urls || []).filter((p) => p && p.url),
});

/**
 * Controlled inputs for the structured on-site / arrival details.
 * Evie reads these fields directly to answer "where do I go?".
 */
export const OnsiteDetailsFields = ({ value = emptyOnsite(), onChange, idPrefix = 'onsite' }) => {
  const set = (k, val) => onChange(k, val);
  const plans = value.floor_plan_urls || [];

  const setPlan = (i, field, val) => {
    const next = plans.map((p, idx) => (idx === i ? { ...p, [field]: val } : p));
    onChange('floor_plan_urls', next);
  };
  const addPlan = () => onChange('floor_plan_urls', [...plans, { name: '', url: '' }]);
  const removePlan = (i) => onChange('floor_plan_urls', plans.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4" data-testid={`${idPrefix}-fields`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={`${idPrefix}-booth`}>Booth / table number</Label>
          <Input
            id={`${idPrefix}-booth`}
            data-testid={`${idPrefix}-booth-number`}
            placeholder="e.g. 1350"
            value={value.booth_number || ''}
            onChange={(e) => set('booth_number', e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-report`}>Reporting location</Label>
          <Input
            id={`${idPrefix}-report`}
            data-testid={`${idPrefix}-reporting-location`}
            placeholder="e.g. GGF booth, Hall B"
            value={value.reporting_location || ''}
            onChange={(e) => set('reporting_location', e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor={`${idPrefix}-infodesk`}>Registration / information desk location</Label>
        <Input
          id={`${idPrefix}-infodesk`}
          data-testid={`${idPrefix}-info-desk-location`}
          placeholder="e.g. Main lobby registration desk, near Entrance A"
          value={value.info_desk_location || ''}
          onChange={(e) => set('info_desk_location', e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor={`${idPrefix}-entrance`}>Entrance instructions</Label>
        <Textarea
          id={`${idPrefix}-entrance`}
          data-testid={`${idPrefix}-entrance-instructions`}
          rows={2}
          placeholder="Which entrance to use, badge pickup, security check-in…"
          value={value.entrance_instructions || ''}
          onChange={(e) => set('entrance_instructions', e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor={`${idPrefix}-directions`}>Onsite directions</Label>
        <Textarea
          id={`${idPrefix}-directions`}
          data-testid={`${idPrefix}-onsite-directions`}
          rows={2}
          placeholder="How to get from the entrance to the reporting location once inside…"
          value={value.onsite_directions || ''}
          onChange={(e) => set('onsite_directions', e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor={`${idPrefix}-parking`}>Parking instructions</Label>
        <Textarea
          id={`${idPrefix}-parking`}
          data-testid={`${idPrefix}-parking-instructions`}
          rows={2}
          placeholder="Where to park, validation, cost, load-in area…"
          value={value.parking_instructions || ''}
          onChange={(e) => set('parking_instructions', e.target.value)}
        />
      </div>

      <div>
        <Label htmlFor={`${idPrefix}-arrival`}>Additional arrival instructions</Label>
        <Textarea
          id={`${idPrefix}-arrival`}
          data-testid={`${idPrefix}-arrival-instructions`}
          rows={2}
          placeholder="Shuttle info, what time to arrive, who to text on arrival…"
          value={value.arrival_instructions || ''}
          onChange={(e) => set('arrival_instructions', e.target.value)}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label>Floor plans / maps</Label>
          <Button
            type="button" variant="outline" size="sm"
            data-testid={`${idPrefix}-add-floor-plan`}
            onClick={addPlan}
          >
            <Plus className="h-4 w-4 mr-1" /> Add map link
          </Button>
        </div>
        {plans.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Add a link to an event floor plan or venue map (PDF or image URL).
          </p>
        )}
        <div className="space-y-2">
          {plans.map((p, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`${idPrefix}-floor-plan-${i}`}>
              <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input
                className="w-1/3"
                placeholder="Label (e.g. Hall B map)"
                data-testid={`${idPrefix}-floor-plan-name-${i}`}
                value={p.name || ''}
                onChange={(e) => setPlan(i, 'name', e.target.value)}
              />
              <Input
                className="flex-1"
                placeholder="https://…"
                data-testid={`${idPrefix}-floor-plan-url-${i}`}
                value={p.url || ''}
                onChange={(e) => setPlan(i, 'url', e.target.value)}
              />
              <Button
                type="button" variant="ghost" size="icon"
                data-testid={`${idPrefix}-remove-floor-plan-${i}`}
                onClick={() => removePlan(i)}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnsiteDetailsFields;
